import type {
  CameraPositionData,
  CellData,
  GridId,
  GridLayout,
  GridMap,
  GridPosition,
  ScreenLayout,
} from "@observe-tech/utils";
import { GRID_COLUMNS, GRID_ROWS, isGridId } from "@observe-tech/utils";

import type { SyncStore as Store } from "../../infra/state";

export const cycleIndex = (
  currentIndex: number,
  length: number,
  direction: "next" | "previous",
): number => {
  if (length === 0) {
    return -1;
  }

  if (currentIndex === -1) {
    return 0;
  }

  if (direction === "next") {
    return (currentIndex + 1) % length;
  }

  return (currentIndex - 1 + length) % length;
};

export type CellWithPosition = {
  position: GridPosition;
  gridId: GridId;
  gridIndex: number;
};

export const getCellsWithData = (
  gridData: GridMap,
  gridLayout: { rows: number; columns: number },
): CellWithPosition[] => {
  const { rows, columns } = gridLayout;
  const cells: CellWithPosition[] = [];

  for (let row = 1; row <= rows; row++) {
    for (let col = 1; col <= columns; col++) {
      const gridId = `${row}-${col}` as GridId;
      const cellData = gridData[gridId];
      if (cellData && cellData.cameras && cellData.cameras.length > 0) {
        cells.push({
          position: { row, column: col } as GridPosition,
          gridId,
          gridIndex: (row - 1) * columns + col,
        });
      }
    }
  }

  return cells;
};

export const findNextCellIndex = (
  cells: CellWithPosition[],
  currentGridId: GridId,
  direction: "next" | "previous",
  columns: number,
): number => {
  if (cells.length === 0) {
    return -1;
  }

  const [currentRow, currentCol] = currentGridId.split("-").map(Number);
  const currentGridIndex = (currentRow - 1) * columns + currentCol;

  const currentIndex = cells.findIndex(
    (cell) => cell.gridIndex === currentGridIndex,
  );

  return cycleIndex(currentIndex, cells.length, direction);
};

export type GridUtils = ReturnType<typeof createGridUtils>;
export const createGridUtils = () => {
  const shouldUseCameraGrid = (layout: ScreenLayout): boolean => {
    return layout.screenData.nCams === 1;
  };

  const gridToUse = (layout: ScreenLayout): GridMap => {
    return shouldUseCameraGrid(layout)
      ? layout.screenData.cameraGrid
      : layout.screenData.cageGrid;
  };

  const positionToGridId = (position: GridPosition): GridId => {
    const gridId = `${position.row}-${position.column}` as GridId;
    if (!isGridId(gridId)) {
      throw new Error(
        `Invalid grid position: ${position.row}-${position.column}. Rows must be one of [${GRID_ROWS.join(", ")}] and columns must be one of [${GRID_COLUMNS.join(", ")}].`,
      );
    }
    return gridId;
  };

  const getSourceScreenId = (
    store: Store,
    cameraData: CameraPositionData,
  ): number | null => {
    if (!cameraData || !cameraData.machineId || !cameraData.cageId) {
      return null;
    }

    const cameraId = cameraData.cameraId;
    const screens = store.get("screenLayouts");

    for (const screenLayout of screens) {
      const grid = gridToUse(screenLayout);
      const cellInScreen = Object.entries(grid).find(
        ([, data]) =>
          data !== null &&
          data !== undefined &&
          data.machineId === cameraData.machineId &&
          data.cageId === cameraData.cageId &&
          // If cameraId is provided, match specific camera
          // If cameraId is undefined, match any camera from the cage
          (cameraId ? data.cameras[0] === cameraId : true),
      );

      if (cellInScreen) {
        return screenLayout.id;
      }
    }

    return null;
  };

  // Helper function to find existing camera/cage data across all screens
  const findExistingCameraPosition = (
    store: Store,
    targetData: CellData,
    excludeGridId?: GridId,
  ): { screenId: number; gridId: GridId; data: CellData } | null => {
    if (!targetData) {
      return null;
    }

    const screens = store.get("screenLayouts");

    for (const screenLayout of screens) {
      const grid = gridToUse(screenLayout);
      const cellInScreen = Object.entries(grid).find(
        ([cellGridId, data]) =>
          cellGridId !== excludeGridId &&
          data !== null &&
          data !== undefined &&
          data.machineId === targetData.machineId &&
          data.cageId === targetData.cageId &&
          (screenLayout.screenData.nCams === 1 &&
          targetData.cameras.length === 1
            ? data.cameras[0] === targetData.cameras[0]
            : true),
      );

      if (cellInScreen) {
        const [gridId, data] = cellInScreen;
        return {
          screenId: screenLayout.id,
          gridId: gridId as GridId,
          data,
        };
      }
    }

    return null;
  };

  // Helper function to adjust focused positions when grid layout changes
  const adjustFocusedPositionsForNewLayout = (
    layout: ScreenLayout,
    gridLayout: GridLayout,
  ): ScreenLayout => {
    const oldColumns = layout.screenData.gridLayout.columns;
    const newColumns = gridLayout.columns;

    if (oldColumns === newColumns) return layout;

    const { halfScreenLeft, halfScreenRight } = layout.screenData;

    // Function to get the column number from a GridId
    const getColumnFromGridId = (gridId: string): number => {
      return parseInt(gridId.split("-")[1], 10);
    };

    // Function to determine if a column is on the left half
    const isLeftHalf = (column: number, totalColumns: number): boolean => {
      return column <= Math.floor(totalColumns / 2);
    };

    let newHalfScreenLeft = halfScreenLeft;
    let newHalfScreenRight = halfScreenRight;
    let needsUpdate = false;

    // Check and adjust halfScreenLeft
    if (halfScreenLeft) {
      const leftColumn = getColumnFromGridId(halfScreenLeft);
      const leftIsOnLeftHalf = isLeftHalf(leftColumn, newColumns);

      if (!leftIsOnLeftHalf) {
        // The focused left cell is now on the right side, swap it
        newHalfScreenRight = halfScreenLeft;
        newHalfScreenLeft = halfScreenRight; // This might be null
        needsUpdate = true;
      }
    }

    // Check and adjust halfScreenRight (only if we haven't already swapped)
    if (!needsUpdate && halfScreenRight) {
      const rightColumn = getColumnFromGridId(halfScreenRight);
      const rightIsOnLeftHalf = isLeftHalf(rightColumn, newColumns);

      if (rightIsOnLeftHalf) {
        // The focused right cell is now on the left side, swap it
        newHalfScreenLeft = halfScreenRight;
        newHalfScreenRight = halfScreenLeft; // This might be null
      }
    }

    // Return a new layout object with updated focused positions
    return {
      ...layout,
      screenData: {
        ...layout.screenData,
        halfScreenLeft: newHalfScreenLeft,
        halfScreenRight: newHalfScreenRight,
      },
    };
  };

  // Ensures consistency when swapping pins bewtween grid positions
  const findExistingPinPosition = (
    store: Store,
    feedlineId: string,
    machineId: number,
    excludeScreenId?: number,
    excludeGridId?: GridId,
  ): { screenId: number; gridId: GridId } | null => {
    const screens = store.get("screenLayouts");
    for (const screenLayout of screens) {
      const grid = gridToUse(screenLayout);
      const match = Object.entries(grid).find(
        ([cellGridId, data]) =>
          !(
            screenLayout.id === excludeScreenId && cellGridId === excludeGridId
          ) &&
          data !== null &&
          data !== undefined &&
          data.pin?.type === "feeding" &&
          data.pin.feedlineId === feedlineId &&
          data.pin.machineId === machineId,
      );
      if (match) {
        return { screenId: screenLayout.id, gridId: match[0] as GridId };
      }
    }
    return null;
  };

  return {
    shouldUseCameraGrid,
    gridToUse,
    positionToGridId,
    getSourceScreenId,
    findExistingCameraPosition,
    findExistingPinPosition,
    adjustFocusedPositionsForNewLayout,
  };
};
